<template>
  <div
    class="tw-block md:tw-grid tw-grid-cols-[1fr_182px] tw-gap-x-[28px] xl:tw-gap-x-[54px] tw-mt-[36px] md:tw-mt-0 tw-mb-[12px] md:tw-mb-[54px]"
  >
    <div class="tw-flex tw-items-start tw-gap-[16px]">
      <div class="tw-w-[calc(100%-84px)] lg:tw-w-full">
        <multiselect
          class="my-multiselect"
          :value="selectedCampaignObject"
          @input="handleCampaignSelect"
          :placeholder="$t('donor_dashboard.select_campaign', 'Select campaign')"
          label="displayName"
          track-by="id"
          :options="campaignOptions"
          :show-labels="false"
          :disabled="loading"
        >
        </multiselect>

        <multiselect
          v-if="campaigns.length > 0 && teamsBySelectedCampaign.length > 0"
          class="my-multiselect tw-mt-[16px] md:tw-mt-[24px]"
          :value="selectedTeamObject"
          @input="handleTeamSelect"
          :placeholder="$t('donor_dashboard.select_team', 'Select team')"
          label="displayName"
          track-by="id"
          :options="teamOptions"
          :show-labels="false"
          :disabled="loading"
        >
        </multiselect>
      </div>
      <div
        @click="menuToggleRoot"
        class="tw-flex tw-items-center tw-justify-end lg:tw-hidden tw-w-[66px] tw-ms-auto tw-py-[10px] md:tw-py-[17px] tw-px-[22px] tw-cursor-pointer"
      >
        <IconBurger
          class="tw-w-[22px] tw-h-[22px] tw-text-[var(--my-white-soft2)] md:tw-text-[var(--my-primary)]"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import multiselect from "vue-multiselect";
import { computed, onMounted, ref } from "vue";
import { useProctectedListUserCampaigns } from "../../compositions/campaigns";
import { useProctectedMyTeamsList } from "../../compositions/my-teams";
import { notificationsMixin } from "@/mixins";
import { getCurrentInstance } from "vue";
import IconBurger from "@/views/my-contacts/icons/burger.vue";
import { useStore } from "@/store/store";

const vm = getCurrentInstance();
const notificationsMixinHandleCatch =
  notificationsMixin.methods.$_notificationsMixin_handleCatch.bind(vm.proxy);

function menuToggleRoot() {
  vm.proxy.$root.$emit("donor-dashboard:menu-toggle");
}

const emit = defineEmits(["selection-changed"]);

const store = useStore();

const loading = ref(false);
const preselectedOption = computed({
  get() {
    return store.state.donorMyContacts.campaignId;
  },
  set(value) {
    store.commit("donorMyContacts/setCampaignId", value);
  },
});
const preselectedTeamOption = computed({
  get() {
    return store.state.donorMyContacts.teamId;
  },
  set(value) {
    store.commit("donorMyContacts/setTeamId", value);
  },
});
const preselectedOptionCurrency = computed({
  get() {
    return store.state.donorMyContacts.campaignCurrency;
  },
  set(value) {
    store.commit("donorMyContacts/setCampaignCurrency", value);
  },
});

const { campaigns, getCampaigns } = useProctectedListUserCampaigns();
const { teams, fetchProtectedMyTeams } = useProctectedMyTeamsList();

function filterTeamsByCampaignId(teams, campaignId) {
  return teams
    .filter((team) => Number(team.campaign.campaign_id) === Number(campaignId))
    .map((team) => team.team);
}

const campaignOptions = computed(() => {
  return campaigns.value.map((campaign) => ({
    id: campaign.id,
    displayName: `(${campaign.id}) ${campaign.attributes.campaigntitle}`,
  }));
});

const selectedCampaignObject = computed(() => {
  if (!preselectedOption.value) return null;
  return campaignOptions.value.find((option) => option.id == preselectedOption.value);
});

const handleCampaignSelect = async (selectedOption) => {
  const campaignId = selectedOption ? selectedOption.id : null;
  await actionLoading(onCampaignAndTeamSelected, campaignId, null);
};

const teamOptions = computed(() => {
  return teamsBySelectedCampaign.value.map((team) => ({
    id: team.id,
    displayName: `(${team.id}) ${team.name}`,
  }));
});

const selectedTeamObject = computed(() => {
  if (!preselectedTeamOption.value) return null;
  return teamOptions.value.find((option) => option.id == preselectedTeamOption.value);
});

const handleTeamSelect = async (selectedOption) => {
  const teamId = selectedOption ? selectedOption.id : null;
  await actionLoading(onCampaignAndTeamSelected, preselectedOption.value, teamId);
};

const teamsBySelectedCampaign = computed(() => {
  return filterTeamsByCampaignId(teams.value, preselectedOption.value);
});

async function actionLoading(fn, ...args) {
  loading.value = true;

  try {
    const { promise, resolve, reject } = Promise.withResolvers();
    const data = await fn(
      {
        resolve,
        reject,
      },
      ...args
    );
    await promise;

    return data;
  } finally {
    loading.value = false;
  }
}

async function onCampaignAndTeamSelected({ resolve, reject }, campaignId, teamId) {
  // Find campaign
  const campaign = campaigns.value.find((el) => Number(el.id) === Number(campaignId));
  if (!campaign) {
    preselectedOption.value = null;
    preselectedOptionCurrency.value = null;
    preselectedTeamOption.value = null;
    emit("selection-changed", {
      context: {
        reject,
      },
      campaign: null,
      team: null,
      campaignCurrency: null,
      campaigns: campaigns.value,
      teams: [],
    });
    return;
  }

  preselectedOption.value = Number(campaign.id);
  preselectedOptionCurrency.value = campaign.attributes.currency;

  let teamsData = [];
  try {
    teamsData = await fetchProtectedMyTeams({
      extend: ["stats", "campaign", "organization", "permissions"],
    });
  } catch (e) {
    notificationsMixinHandleCatch(e);
    emit("selection-changed", {
      context: {
        reject,
      },
      campaign,
      team: null,
      campaignCurrency: campaign.attributes.currency,
      campaigns: campaigns.value,
      teams: [],
    });
    return;
  }

  const teamsByCampaign = filterTeamsByCampaignId(teamsData, preselectedOption.value);
  let validTeamId = teamId;
  if (!validTeamId || !teamsByCampaign.some((t) => t.id === teamId)) {
    validTeamId = teamsByCampaign[0]?.id || null;
  }
  preselectedTeamOption.value = validTeamId ? Number(validTeamId) : null;
  const team = teamsByCampaign.find((t) => t.id === validTeamId) || null;

  emit("selection-changed", {
    context: {
      resolve,
    },
    campaign,
    team,
    campaignCurrency: campaign.attributes.currency,
    campaigns: campaigns.value,
    teams: teamsByCampaign,
  });
}

onMounted(() => {
  loading.value = true;

  const { promise, resolve } = Promise.withResolvers();
  getCampaigns({
    page: 0,
    per_page: 0,
  })
    .then(() => {
      // Use localStorage value if available, otherwise fallback to first item
      let initialCampaignId = preselectedOption.value;
      if (!initialCampaignId && campaigns.value.length > 0) {
        initialCampaignId = campaigns.value[0]?.id || 0;
      }
      return onCampaignAndTeamSelected({ resolve }, initialCampaignId, preselectedTeamOption.value);
    })
    .then(() => promise)
    .catch((e) => notificationsMixinHandleCatch(e))
    .finally(() => {
      loading.value = false;
    });
});
</script>

<style lang="scss" scoped>
::v-deep .multiselect.my-multiselect {
  @apply tw-rounded-full;

  .multiselect__tags {
    @apply tw-flex tw-items-center tw-min-h-[46px] md:tw-min-h-[62px] tw-ps-[24px] tw-pe-[48px] md:tw-ps-[44px] md:tw-px-[52px] tw-rounded-full tw-border-[var(--my-white-soft)] tw-bg-[var(--my-white-soft)] tw-cursor-pointer;
  }

  .multiselect__select {
    @apply tw-top-[50%] tw-right-[8px] md:tw-right-[16px] tw-translate-y-[-50%];

    &:before {
      @apply tw-border-t-[var(--my-primary)];
    }
  }

  &.multiselect--active .multiselect__select {
    @apply tw-top-[50%];
    transform: rotateZ(180deg) translateY(50%);
  }

  .multiselect__content-wrapper {
    @apply tw-rounded-[20px];
  }

  .multiselect__single,
  .multiselect__input,
  .multiselect__placeholder {
    @apply md:tw-min-h-[30px] tw-text-[18px] md:tw-text-[24px] tw-text-[var(--my-text-color)] tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis md:tw-leading-[30px] tw-bg-[var(--my-white-soft)] tw-border-[var(--my-white-soft)] tw-cursor-pointer;
  }

  .multiselect__option--highlight {
    @apply tw-bg-[var(--my-primary)];
  }

  .multiselect__spinner {
    @apply tw-top-[50%] tw-right-[8px] md:tw-right-[16px] tw-translate-y-[-50%] tw-bg-[var(--my-white-soft)];
  }

  &.multiselect--disabled {
    .multiselect__select {
      @apply tw-bg-[var(--my-white-soft)];
    }
  }
}
</style>
